import { useMemo, useState } from 'react'

import type { UserListItem } from '#/shared/api/generated/auth/models'
import { useListUsersApiV1UsersGet } from '#/shared/api/generated/auth/theCargoAuth'
import { useDebouncedValue } from '#/shared/lib/hooks'
import type { MultiSelectOption } from '#/shared/ui/multi-select'

const USERS_PAGE_SIZE = 100
const SEARCH_DEBOUNCE_MS = 250

export interface UseUserOptionsResult {
  options: MultiSelectOption[]
  isLoading: boolean
  searchValue: string
  onSearchChange: (next: string) => void
}

function formatUserLabel(user: UserListItem): string {
  const fullName = [user.first_name, user.last_name]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(' ')
    .trim()
  return fullName.length > 0 ? fullName : user.email
}

export function useUserOptions(): UseUserOptionsResult {
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearch = useDebouncedValue(searchValue, SEARCH_DEBOUNCE_MS)
  const trimmed = debouncedSearch.trim()

  const query = useListUsersApiV1UsersGet({
    size: USERS_PAGE_SIZE,
    q: trimmed.length > 0 ? trimmed : undefined,
  })

  const options = useMemo<MultiSelectOption[]>(() => {
    const items = query.data?.data.items ?? []
    return items
      .filter((user) => user.is_active)
      .map((user) => ({
        value: user.id,
        label: formatUserLabel(user),
      }))
  }, [query.data])

  return {
    options,
    isLoading: query.isLoading || query.isFetching,
    searchValue,
    onSearchChange: setSearchValue,
  }
}
